import { array, boolean, date, InferType, mixed, object, string } from "yup";

// Configuration for components
const baseConfigurationItemSchema = object({
  type: string().required(),
  value: mixed<string | boolean>().required(),
});

const configurationItemSchema = object({
  id: string().uuid().required(),
  componentId: string().uuid().required(),
}).concat(baseConfigurationItemSchema);

// Configuration for sub components
const baseSubComponentConfigurationItemSchema = object({
  type: string().required(),
  value: mixed<string | boolean>().required(),
});

const subComponentConfigurationItemSchema = object({
  id: string().uuid().required(),
  subComponentId: string().uuid().required(),
}).concat(baseSubComponentConfigurationItemSchema);

export interface ConfigurationItem
  extends InferType<typeof configurationItemSchema> {}

export enum ComponentType {
  HARDWARE = "HARDWARE",
  SOFTWARE = "SOFTWARE",
}

export enum SubComponentType {
  MECHANICAL = "MECHANICAL",
  ELECTRICAL = "ELECTRICAL",
  FIRMWARE = "FIRMWARE",
  SOFTWARE = "SOFTWARE",
}

export enum ComponentConfigurationType {
  HAS_AI = "hasAI",
  IS_IMPLANTABLE = "isImplantable",
  IS_STERILIZATION_INTENDED = "isSterilizationIntended",
  HAS_HUMAN_CONTACT = "hasHumanContact",
}

export enum SubComponentConfigurationType {
  IS_IMPLANTABLE = "isImplantable",
  HAS_AI = "hasAI",
}

const baseSubComponentSchema = object({
  name: string().optional(),
  description: string().required(),
  type: mixed<SubComponentType>()
    .oneOf(Object.values(SubComponentType))
    .required(),
});

const createSubComponentSchema = baseSubComponentSchema.concat(
  object({
    configuration: array(baseSubComponentConfigurationItemSchema).optional(),
  })
);

export interface CreateSubComponent
  extends InferType<typeof createSubComponentSchema> {}

const subComponentSchema = baseSubComponentSchema.concat(
  object({
    id: string().uuid().required(),
    configuration: array(subComponentConfigurationItemSchema).required(),
    componentId: string().uuid().required(),
  })
);

export interface SubComponent extends InferType<typeof subComponentSchema> {}

const baseComponentSchema = object({
  name: string().required(),
  description: string().required(),
  type: mixed<ComponentType>().oneOf(Object.values(ComponentType)).required(),
});

const componentSchema = baseComponentSchema.concat(
  object({
    id: string().uuid().required(),
    configuration: array(configurationItemSchema).required(),
    subComponents: array(subComponentSchema).required(),
    deviceId: string().uuid().required(),
  })
);
export interface Component extends InferType<typeof componentSchema> {}

const createComponentSchema = baseComponentSchema.concat(
  object({
    configuration: array(baseConfigurationItemSchema).required(),
    subComponents: array(createSubComponentSchema).optional(),
  })
);

export interface CreateComponent
  extends InferType<typeof createComponentSchema> {}

export const baseDeviceSchema = object({
  name: string().required(),
  description: string().required(),
  isDemoDevice: boolean().optional(),
  isMDR: boolean().required(),
  isFDA: boolean().required(),
  isIVDR: boolean().required(),
  isUK: boolean().default(false).required(),
  isCanada: boolean().default(false).required(),
});

export const deviceSchema = baseDeviceSchema.concat(
  object({
    id: string().required(),
    components: array(componentSchema).required(),
    createdBy: string().required(),
    createdAt: date().required(),
    updatedAt: date().required(),
  })
);
export interface Device extends InferType<typeof deviceSchema> {}

export const createDeviceSchema = baseDeviceSchema.concat(
  object({
    components: array(createComponentSchema).required(),
  })
);
export interface CreateDevice extends InferType<typeof createDeviceSchema> {}

export const createDeviceResponseSchema = object({
  result: createDeviceSchema,
});

export interface CreateDeviceResponse
  extends InferType<typeof createDeviceResponseSchema> {}
